import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Download, Mail, Sparkles, RefreshCw, ArrowLeft, X, Plus } from "lucide-react";
import axios from "axios";
import jsPDF from "jspdf";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoDark } from "@/components/brand/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

const ZONES = [
  { num: 1, name: "Foundation", color: "#E24B4A" },
  { num: 2, name: "Building", color: "#EF9F27" },
  { num: 3, name: "Practising", color: "#CBCB2A" },
  { num: 4, name: "Mastering", color: "#639922" },
  { num: 5, name: "Pioneering", color: "#C9A84C" },
];

const DIMS = ["Think Power", "Do Power", "Connect Power", "Adapt Power"];
const DIM_COLORS = { "Think Power": "#14B8A6", "Do Power": "#8A9A5B", "Connect Power": "#5582B7", "Adapt Power": "#9333EA" };
const RESP_COLORS = { Cultivate: "#639922", Capture: "#E24B4A", Contract: "#EF9F27", Configure: "#5582B7" };

function ScoreCard({ label, value, threshold = "neutral" }) {
  let color = "#0F1E35";
  if (threshold === "fit") {
    color = value > 65 ? "#639922" : value >= 40 ? "#EF9F27" : "#E24B4A";
  } else if (threshold === "delta") {
    color = value >= 0 ? "#639922" : value >= -15 ? "#EF9F27" : "#E24B4A";
  }
  return (
    <div className="bg-white rounded-xl shadow-md p-6 text-center border-t-4" style={{ borderColor: color }}>
      <p className="font-dm text-navy/50 text-xs uppercase tracking-wide mb-2">{label}</p>
      <p className="font-playfair text-5xl font-bold" style={{ color }}>{value > 0 && threshold === "delta" ? `+${value}` : value}</p>
    </div>
  );
}

function EmployeeReportCard({ er, employeeIdx, reportId, isRescan, onCoaching, onRescan, onMovementReport, movementBusy, movementError, isSharedView }) {
  const emp = er.employee || {};
  const [futureHorizon, setFutureHorizon] = useState(er.future_horizon);
  const delta = futureHorizon - er.present_fit;

  return (
    <div className="bg-cream rounded-2xl p-6 mb-6">
      <h2 className="font-playfair text-navy text-2xl font-bold mb-1">{emp.name}</h2>
      <p className="font-dm text-navy/60 text-sm mb-6">{emp.role}{emp.department ? ` · ${emp.department}` : ""}</p>

      {/* Section 1 — Scores */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <ScoreCard label="Present Fit Score" value={er.present_fit} threshold="fit" />
        <div className="bg-white rounded-xl shadow-md p-6 text-center border-t-4" style={{ borderColor: futureHorizon > 65 ? "#639922" : futureHorizon >= 40 ? "#EF9F27" : "#E24B4A" }}>
          <p className="font-dm text-navy/50 text-xs uppercase tracking-wide mb-2">Future Horizon</p>
          <input type="number" value={futureHorizon} onChange={(e) => setFutureHorizon(Math.max(0, Math.min(100, Number(e.target.value))))}
            className="font-playfair text-5xl font-bold text-center w-full border-none bg-transparent focus:outline-none" style={{ color: futureHorizon > 65 ? "#639922" : futureHorizon >= 40 ? "#EF9F27" : "#E24B4A" }} />
          <p className="font-dm text-navy/40 text-[10px] mt-1">Projected 12-month gap (editable)</p>
        </div>
        <ScoreCard label="Readiness Delta" value={delta} threshold="delta" />
      </div>

      {/* Section 2 — 4D Capability Portrait */}
      <h3 className="font-playfair text-navy text-xl font-bold mb-3">4D Capability Portrait</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {DIMS.map((d) => {
          const ds = er.dimension_scores?.[d] || { score: 0, target: 0, skills: [] };
          return (
            <div key={d} className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="font-dm text-xs font-bold uppercase px-2 py-1 rounded-full text-white" style={{ backgroundColor: DIM_COLORS[d] }}>{d}</span>
                <p className="font-playfair text-navy text-xl font-bold">{ds.score}<span className="text-sm text-navy/50">/{ds.target}</span></p>
              </div>
              <div className="space-y-2">
                {(ds.skills || []).map((s, i) => (
                  <div key={i} className="text-xs">
                    <p className="font-dm text-navy/80 mb-1 truncate">{s.skill}</p>
                    <div className="flex items-center gap-1">
                      {ZONES.map((z) => (
                        <div key={z.num} className="flex-1 h-2 rounded-full" style={{ backgroundColor: s.now >= z.num ? z.color : "#E5E7EB" }} />
                      ))}
                      <div className="w-2 h-2 rounded-full bg-navy ml-2" title={`Required: ${ZONES[s.required - 1].name}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Section 3 — Dual Horizon Table */}
      <h3 className="font-playfair text-navy text-xl font-bold mb-3">Skill-by-Skill Gap View</h3>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
        <table className="w-full">
          <thead className="bg-navy/5">
            <tr><th className="text-left font-dm text-navy/60 text-xs uppercase py-3 px-4">Skill</th><th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Dim</th><th className="text-center font-dm text-navy/60 text-xs py-3 px-4">Now</th><th className="text-center font-dm text-navy/60 text-xs py-3 px-4">Required</th><th className="text-center font-dm text-navy/60 text-xs py-3 px-4">Gap</th></tr>
          </thead>
          <tbody>
            {(er.skill_rows || []).map((s, i) => {
              const gapColor = s.gap === 0 ? "#639922" : s.gap === -1 ? "#EF9F27" : "#E24B4A";
              const gapLabel = s.gap === 0 ? "On target" : s.gap === -1 ? "Developing" : "Critical gap";
              return (
                <tr key={i} className="border-b border-navy/5">
                  <td className="font-dm text-navy text-sm py-3 px-4">{s.skill}</td>
                  <td className="py-3 px-4"><span className="font-dm text-[10px] text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: DIM_COLORS[s.dimension] || "#888" }}>{(s.dimension || "").replace(" Power", "")}</span></td>
                  <td className="text-center font-dm text-navy text-sm py-3 px-4 font-bold">{s.now}</td>
                  <td className="text-center font-dm text-navy/70 text-sm py-3 px-4">{s.required}</td>
                  <td className="text-center font-dm text-sm py-3 px-4 font-bold" style={{ color: gapColor }}>{s.gap >= 0 ? `+${s.gap}` : s.gap} <span className="text-[10px] font-normal">· {gapLabel}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Section 4 — Business Impact Signals */}
      {(er.impact_signals || []).length > 0 && (<>
        <h3 className="font-playfair text-navy text-xl font-bold mb-3">What These Gaps Mean for the Business</h3>
        <div className="space-y-3 mb-8">
          {er.impact_signals.map((sig, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-4 border-l-4" style={{ borderColor: sig.risk === "high" ? "#E24B4A" : "#EF9F27" }} data-testid={`impact-signal-${i}`}>
              <p className="font-dm text-navy text-xs font-bold uppercase mb-1">{sig.skill}</p>
              <p className="font-dm text-navy/80 text-sm">{sig.text}</p>
            </div>
          ))}
        </div>
      </>)}

      {/* Section 5 — 4C */}
      {(er.fourc || []).length > 0 && (<>
        <h3 className="font-playfair text-navy text-xl font-bold mb-3">Recommended Action for Each Critical Gap</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          {er.fourc.map((c, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-4 border-l-4" style={{ borderColor: RESP_COLORS[c.response] || "#888" }}>
              <p className="font-dm text-navy/60 text-xs uppercase font-bold mb-1">{c.skill}</p>
              <p className="font-playfair text-navy text-2xl font-bold mb-1" style={{ color: RESP_COLORS[c.response] }}>{c.response}</p>
              <p className="font-dm text-navy/70 text-sm">{c.explanation}</p>
            </div>
          ))}
        </div>
      </>)}

      {/* Section 6 — Growth Path */}
      {(er.growth_path || []).length > 0 && (<>
        <h3 className="font-playfair text-navy text-xl font-bold mb-3">Development Roadmap</h3>
        <div className="space-y-3 mb-8">
          {er.growth_path.map((g, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <p className="font-dm text-navy font-bold text-sm">{g.skill}</p>
                <span className="font-dm text-navy/50 text-xs">{g.from} → {g.to}</span>
                <span className="bg-gold/20 text-gold font-dm text-[10px] font-bold px-2 py-0.5 rounded-full">{g.intervention}</span>
                <span className="bg-navy/10 text-navy/70 font-dm text-[10px] px-2 py-0.5 rounded-full">{g.timeline}</span>
              </div>
              <p className="font-dm text-navy/70 text-xs italic">✓ {g.success_signal}</p>
            </div>
          ))}
        </div>
      </>)}

      {/* Section 7 — Next level readiness */}
      <div className="bg-navy rounded-xl p-6 text-center mb-8">
        <p className="font-dm text-gold/80 text-xs uppercase tracking-wide mb-2">Promotion Readiness</p>
        <p className="font-playfair text-white text-6xl font-bold">{er.readiness_pct}%</p>
        <p className="font-dm text-gold font-bold text-lg mt-2">{er.readiness_label}</p>
        <p className="font-dm text-white/70 text-sm mt-3 max-w-2xl mx-auto italic">{er.readiness_sentence}</p>
      </div>

      <div className="flex flex-wrap gap-3 justify-center">
        <button onClick={onCoaching} className="bg-gold text-navy font-dm font-bold px-5 py-2.5 rounded-lg hover:bg-gold/90 text-sm flex items-center gap-2"><Sparkles className="w-4 h-4" />Start Coaching</button>
        {!isSharedView && (er.fourc || [])[0]?.skill && (
          <a href={`/coaching/start?topic=${encodeURIComponent((er.fourc || [])[0].skill)}&capscan_id=${reportId}&gap=${encodeURIComponent((er.fourc || [])[0].skill)}`} data-testid={`coach-on-gap-${employeeIdx}`}
            className="border border-gold text-gold font-dm font-bold px-5 py-2.5 rounded-lg hover:bg-gold hover:text-navy text-sm flex items-center gap-2 transition-colors">
            <Sparkles className="w-4 h-4" />Coach me on &quot;{(er.fourc || [])[0].skill}&quot;
          </a>
        )}
        {!isSharedView && (
          <button onClick={() => onRescan(employeeIdx)} data-testid={`rescan-btn-${employeeIdx}`}
            className="border border-navy text-navy font-dm font-bold px-5 py-2.5 rounded-lg hover:bg-navy hover:text-gold text-sm flex items-center gap-2 transition-colors">
            <RefreshCw className="w-4 h-4" />Re-scan {emp.name?.split(" ")[0] || "person"} <span className="text-[10px] bg-gold/20 text-gold px-1.5 py-0.5 rounded font-bold">1 token</span>
          </button>
        )}
        {!isSharedView && isRescan && (
          <button onClick={() => onMovementReport(employeeIdx)} disabled={movementBusy} data-testid={`movement-report-btn-${employeeIdx}`}
            className="bg-navy text-cream font-dm font-bold px-5 py-2.5 rounded-lg hover:bg-navy/90 text-sm flex items-center gap-2 transition-colors disabled:opacity-60">
            <Sparkles className="w-4 h-4" />{movementBusy ? "Generating…" : "Generate Movement Report"} <span className="text-[10px] bg-gold/20 text-gold px-1.5 py-0.5 rounded font-bold">1 token</span>
          </button>
        )}
      </div>
      {movementError && <p className="font-dm text-red-600 text-sm mt-3 text-center" data-testid={`movement-error-${employeeIdx}`}>{movementError}</p>}
    </div>
  );
}

export default function CapScanReportPage() {
  usePageTitle("4D CapScan Report");
  const { report_id } = useParams();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get("share");
  const isSharedView = Boolean(shareToken);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [showCoaching, setShowCoaching] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [shareEmails, setShareEmails] = useState([""]);
  const [shareMessage, setShareMessage] = useState("");
  const [shareSending, setShareSending] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [rescanError, setRescanError] = useState("");
  const [movementBusy, setMovementBusy] = useState(false);
  const [movementError, setMovementError] = useState("");
  const [movementModal, setMovementModal] = useState(null);  // { employeeIdx } when open
  const [emailToManager, setEmailToManager] = useState(true);
  const [managerEmail, setManagerEmail] = useState("");
  const [hrEmail, setHrEmail] = useState("");

  useEffect(() => {
    if (isSharedView) {
      axios.get(`${API}/api/capscan/report/shared/${shareToken}`)
        .then((r) => setReport(r.data))
        .catch(() => navigate("/"));
      return;
    }
    if (report_id) {
      axios.get(`${API}/api/capscan/report/${report_id}`, { withCredentials: true })
        .then((r) => setReport(r.data))
        .catch(() => navigate("/dashboard?tab=capscan"));
    }
  }, [report_id, navigate, shareToken, isSharedView]);

  if (!report) return <div className="min-h-screen bg-cream flex items-center justify-center pt-16"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  const handleRescan = async (employeeIdx) => {
    setRescanError("");
    try {
      const r = await axios.post(`${API}/api/capscan/rescan`, { report_id, employee_index: employeeIdx }, { withCredentials: true });
      navigate(`/capscan/rate/${r.data.scan_id}`);
    } catch (e) {
      setRescanError(e?.response?.data?.detail || "Failed to start re-scan");
    }
  };

  const handleMovementReport = async (employeeIdx) => {
    setMovementError("");
    setMovementBusy(true);
    try {
      const body = { rescan_report_id: report_id, employee_index: employeeIdx };
      if (emailToManager && (managerEmail.trim() || hrEmail.trim())) {
        body.email_to_manager = true;
        if (managerEmail.trim()) body.manager_email = managerEmail.trim();
        if (hrEmail.trim()) body.hr_email = hrEmail.trim();
      }
      const r = await axios.post(`${API}/api/movement-report/generate`, body, { withCredentials: true });
      navigate(`/capscan/movement-report/${r.data.report_id}`);
    } catch (e) {
      setMovementError(e?.response?.data?.detail || "Failed to generate movement report");
    } finally {
      setMovementBusy(false);
      setMovementModal(null);
    }
  };

  const handleSendShare = async () => {
    setShareSending(true);
    setShareResult(null);
    try {
      const emails = shareEmails.map((e) => e.trim()).filter((e) => e && e.includes("@"));
      if (emails.length === 0) {
        setShareResult({ error: "Please enter at least one valid email address" });
        setShareSending(false);
        return;
      }
      const r = await axios.post(`${API}/api/capscan/report/${report_id}/share`, { emails, message: shareMessage }, { withCredentials: true });
      setShareResult({ sent: r.data.sent || [], failed: r.data.failed || [], share_url: r.data.share_url });
    } catch (e) {
      setShareResult({ error: e?.response?.data?.detail || "Failed to send" });
    } finally {
      setShareSending(false);
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;

    // Render the navy header bar on the current page
    const drawHeader = () => {
      doc.setFillColor(15, 30, 53); doc.rect(0, 0, pageWidth, 70, "F");
      // RoleIQ wordmark (Georgia/serif-style via helvetica + manual triangle hint above the I)
      doc.setFont("helvetica", "bold"); doc.setFontSize(22);
      doc.setTextColor(255, 255, 255); doc.text("RoleIQ", margin, 42);
      // Tagline under wordmark
      doc.setFont("helvetica", "normal"); doc.setFontSize(7);
      doc.setTextColor(201, 168, 76); doc.text("AI-POWERED INTELLIGENCE", margin, 56);
      // Right-side label
      doc.setFontSize(10); doc.setTextColor(201, 168, 76);
      doc.text("CONFIDENTIAL — 4D CapScan Report", pageWidth - margin, 42, { align: "right" });
      // Gold rule under header
      doc.setDrawColor(201, 168, 76); doc.setLineWidth(2);
      doc.line(0, 70, pageWidth, 70);
    };

    // Footer on every page
    const drawFooter = () => {
      doc.setFillColor(10, 22, 40); doc.rect(0, pageHeight - 32, pageWidth, 32, "F");
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      doc.setTextColor(255, 255, 255); doc.text("RoleIQ", margin, pageHeight - 14);
      doc.setTextColor(255, 255, 255); doc.setFontSize(8);
      doc.text("Confidential — Generated by RoleIQ", pageWidth / 2, pageHeight - 14, { align: "center" });
      doc.setTextColor(201, 168, 76);
      doc.text("roleiq.pro", pageWidth - margin, pageHeight - 14, { align: "right" });
    };

    drawHeader();
    let y = 100;
    (report.employee_reports || []).forEach((er, idx) => {
      if (idx > 0) { drawFooter(); doc.addPage(); drawHeader(); y = 100; }
      doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(15, 30, 53);
      doc.text(`${er.employee.name || "Employee"} — ${er.employee.role || ""}`, margin, y); y += 24;
      doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60);
      doc.text(`Present Fit: ${er.present_fit}  Future Horizon: ${er.future_horizon}  Delta: ${er.readiness_delta}`, margin, y); y += 18;
      doc.text(`Promotion Readiness: ${er.readiness_pct}% — ${er.readiness_label}`, margin, y); y += 24;
      doc.setFont("helvetica", "bold"); doc.text("Skill-by-Skill Gap View", margin, y); y += 16;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      (er.skill_rows || []).forEach((s) => {
        if (y > pageHeight - 60) { drawFooter(); doc.addPage(); drawHeader(); y = 100; }
        doc.text(`${s.skill} — Now: ${s.now} | Req: ${s.required} | Gap: ${s.gap}`, margin, y); y += 13;
      });
      y += 8;
      if ((er.fourc || []).length) {
        doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.text("4C Recommendations", margin, y); y += 16;
        doc.setFontSize(9); doc.setFont("helvetica", "normal");
        er.fourc.forEach((c) => {
          if (y > pageHeight - 60) { drawFooter(); doc.addPage(); drawHeader(); y = 100; }
          const lines = doc.splitTextToSize(`${c.skill}: ${c.response} — ${c.explanation}`, pageWidth - margin * 2);
          doc.text(lines, margin, y); y += lines.length * 12 + 4;
        });
      }
    });
    drawFooter();
    doc.save(`RoleIQ_CapScan_Report_${report_id}.pdf`);
  };

  const ta = report.team_analysis;

  return (
    <div className="min-h-screen bg-white pt-24 pb-12">
      {/* Branded report header */}
      <div className="max-w-5xl mx-auto px-4 mb-6">
        <div className="bg-navy rounded-2xl px-6 py-4 flex items-center justify-between flex-wrap gap-3" data-testid="capscan-report-header">
          <LogoDark width={180} height={42} />
          <span className="font-dm text-gold/80 text-[11px] uppercase tracking-[0.2em]">4D CapScan Report</span>
        </div>
        <div style={{ height: "1px", background: "rgba(201,168,76,0.3)" }} className="mt-0" />
      </div>
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => isSharedView ? navigate("/") : navigate("/dashboard?tab=capscan")} className="text-navy/60 hover:text-gold font-dm text-sm flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> {isSharedView ? "RoleIQ Home" : "My CapScan"}</button>
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleDownloadPDF} data-testid="download-pdf-btn" className="bg-navy text-gold font-dm font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-2"><Download className="w-4 h-4" />PDF</button>
            {!isSharedView && (
              <>
                <button onClick={() => { setShareModal(true); setShareResult(null); }} data-testid="share-report-btn" className="border border-navy text-navy font-dm font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-2"><Mail className="w-4 h-4" />Share</button>
                <button onClick={() => setShowCoaching(true)} className="border border-gold text-gold font-dm font-bold text-sm px-4 py-2 rounded-lg flex items-center gap-2"><Sparkles className="w-4 h-4" />Coaching</button>
              </>
            )}
          </div>
        </div>

        {isSharedView && (
          <div className="bg-gold/10 border-l-4 border-gold rounded-r-lg p-4 mb-6">
            <p className="font-dm text-navy text-sm"><strong>View-only shared report</strong> — this link grants read-only access. To create your own CapScan, <Link to="/capscan" className="text-gold font-bold underline">visit RoleIQ</Link>.</p>
          </div>
        )}

        {rescanError && <p className="font-dm text-red-600 text-sm mb-4" data-testid="rescan-error">{rescanError}</p>}

        <h1 className="font-playfair text-navy text-3xl font-bold mb-2">4D CapScan Report</h1>
        <p className="font-dm text-navy/60 text-sm mb-8">{report.map_name} · {new Date(report.created_at).toLocaleDateString("en-IN")}</p>

        {/* Team-level sections (if team scan) */}
        {ta && (
          <>
            <div className="bg-navy rounded-2xl p-6 mb-6 text-white">
              <h2 className="font-playfair text-gold text-xl font-bold mb-4">Team Capability Overview</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr><th className="text-left font-dm text-gold/70 text-xs py-2 pr-3">Employee</th>{DIMS.map((d) => <th key={d} className="text-center font-dm text-gold/70 text-xs py-2 px-2">{d.replace(" Power", "")}</th>)}</tr></thead>
                  <tbody>
                    {ta.heatmap.map((row, i) => (
                      <tr key={i}>
                        <td className="font-dm text-white text-sm py-2 pr-3">{row.name}</td>
                        {row.cells.map((c, j) => (<td key={j} className="text-center py-2"><div className="w-10 h-10 rounded mx-auto flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: ZONES[c.zone - 1]?.color || "#888" }}>{c.zone}</div></td>))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="font-dm text-white/80 text-sm mt-5 italic">{ta.team_signal}</p>
            </div>

            {ta.tni && ta.tni.length > 0 && (
              <div className="bg-cream rounded-xl p-6 mb-6">
                <h3 className="font-playfair text-navy text-lg font-bold mb-3">Top 3 Development Priorities for the Team</h3>
                <div className="space-y-2">
                  {ta.tni.map((t) => (
                    <div key={t.priority} className="bg-white rounded-lg p-3 flex flex-wrap items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-navy text-gold flex items-center justify-center font-bold">{t.priority}</div>
                      <p className="font-dm text-navy font-bold text-sm flex-1">{t.skill}</p>
                      <span className="bg-gold/20 text-gold font-dm text-xs font-bold px-2 py-0.5 rounded-full">{t.intervention}</span>
                      <span className="bg-navy/10 font-dm text-xs text-navy/70 px-2 py-0.5 rounded-full">{t.timeline}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ta.mentor_pairs && ta.mentor_pairs.length > 0 && (
              <div className="bg-cream rounded-xl p-6 mb-6">
                <h3 className="font-playfair text-navy text-lg font-bold mb-3">Peer Learning Opportunities</h3>
                <ul className="space-y-2">
                  {ta.mentor_pairs.map((p, i) => (
                    <li key={i} className="font-dm text-navy/80 text-sm">{p.mentor} can mentor <strong>{p.mentee}</strong> on <em>{p.skill}</em></li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {(report.employee_reports || []).map((er, i) => (
          <EmployeeReportCard key={i} er={er} employeeIdx={i} reportId={report._id} isRescan={!!report.is_rescan} onCoaching={() => setShowCoaching(true)} onRescan={handleRescan} onMovementReport={(idx) => setMovementModal({ employeeIdx: idx })} movementBusy={movementBusy} movementError={movementError} isSharedView={isSharedView} />
        ))}
      </div>

      {movementModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !movementBusy && setMovementModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="movement-modal">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-gold" />
              <h2 className="font-playfair text-navy text-xl font-bold">Generate Movement Report</h2>
            </div>
            <p className="font-dm text-navy/60 text-sm mb-5">
              Compares this re-scan to the original. Costs <strong>1 Intelligence Token</strong>. Optionally email a board-ready copy to the manager and HR partner.
            </p>
            <label className="flex items-start gap-2 mb-3 cursor-pointer">
              <input type="checkbox" checked={emailToManager} onChange={(e) => setEmailToManager(e.target.checked)}
                data-testid="movement-email-checkbox" className="mt-1 accent-navy" />
              <span className="font-dm text-navy text-sm font-semibold">Email a copy to the manager (CC HR partner)</span>
            </label>
            {emailToManager && (
              <div className="space-y-2.5 mb-4 pl-6">
                <input type="email" value={managerEmail} onChange={(e) => setManagerEmail(e.target.value)}
                  placeholder="manager@company.com" data-testid="movement-manager-email"
                  className="w-full px-3 py-2 border border-navy/20 rounded-lg font-dm text-sm focus:border-gold focus:outline-none" />
                <input type="email" value={hrEmail} onChange={(e) => setHrEmail(e.target.value)}
                  placeholder="hr@company.com (optional CC)" data-testid="movement-hr-email"
                  className="w-full px-3 py-2 border border-navy/20 rounded-lg font-dm text-sm focus:border-gold focus:outline-none" />
              </div>
            )}
            {movementError && <p className="font-dm text-red-600 text-sm mb-3">{movementError}</p>}
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setMovementModal(null)} disabled={movementBusy}
                className="border border-navy/20 text-navy/60 font-dm font-bold px-5 py-2 rounded-lg text-sm">
                Cancel
              </button>
              <button onClick={() => handleMovementReport(movementModal.employeeIdx)} disabled={movementBusy}
                data-testid="movement-modal-generate"
                className="bg-navy text-cream font-dm font-bold px-5 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">
                <Sparkles className="w-4 h-4" />{movementBusy ? "Generating…" : "Generate (1 token)"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCoaching && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCoaching(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md p-8 text-center" onClick={(e) => e.stopPropagation()}>
            <Sparkles className="w-12 h-12 text-gold mx-auto mb-3" />
            <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Take this into coaching</h2>
            <p className="font-dm text-navy/60 text-sm mb-5">Continue this conversation with one of seven AI coaches. Your first session is free.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              <button onClick={() => { setShowCoaching(false); navigate("/coaching/start"); }} className="bg-navy text-gold font-dm font-bold px-6 py-2.5 rounded-lg">Start Coaching</button>
              <button onClick={() => setShowCoaching(false)} className="border border-navy/20 text-navy/60 font-dm font-bold px-6 py-2.5 rounded-lg">Not now</button>
            </div>
          </div>
        </div>
      )}

      {shareModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !shareSending && setShareModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
            <h2 className="font-playfair text-navy text-xl font-bold mb-1">Share Report by Email</h2>
            <p className="font-dm text-navy/60 text-sm mb-4">Send a private view-only link of this report (up to 10 recipients).</p>

            {!shareResult?.sent ? (
              <>
                {shareEmails.map((em, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input type="email" placeholder={`recipient${i + 1}@example.com`} value={em} data-testid={`share-email-${i}`}
                      onChange={(e) => { const c = [...shareEmails]; c[i] = e.target.value; setShareEmails(c); }}
                      className="flex-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
                    {shareEmails.length > 1 && (
                      <button onClick={() => setShareEmails(shareEmails.filter((_, idx) => idx !== i))} className="text-red-500 hover:text-red-700 px-2"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                {shareEmails.length < 10 && (
                  <button onClick={() => setShareEmails([...shareEmails, ""])} data-testid="add-share-email"
                    className="text-gold font-dm text-xs font-bold mb-3 flex items-center gap-1 hover:text-gold/80"><Plus className="w-3 h-3" /> Add another recipient</button>
                )}

                <label className="font-dm text-navy/70 text-xs font-semibold block mb-1 mt-3">Personal message (optional)</label>
                <textarea value={shareMessage} onChange={(e) => setShareMessage(e.target.value)} placeholder="Hi team, sharing the capability scan we discussed..."
                  rows={3} data-testid="share-message"
                  className="w-full border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none resize-none mb-3" />

                {shareResult?.error && <p className="font-dm text-red-600 text-xs mb-3" data-testid="share-error">{shareResult.error}</p>}

                <div className="flex gap-3 mt-2">
                  <button onClick={() => setShareModal(false)} disabled={shareSending} className="flex-1 font-dm text-navy/60 py-2.5 hover:text-navy">Cancel</button>
                  <button onClick={handleSendShare} disabled={shareSending} data-testid="send-share-btn" className="flex-1 bg-navy text-gold font-dm font-bold py-2.5 rounded-lg disabled:opacity-50">
                    {shareSending ? "Sending..." : "Send Link"}
                  </button>
                </div>
              </>
            ) : (
              <div data-testid="share-success">
                <div className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4 mb-4">
                  <p className="font-dm text-green-800 text-sm font-bold mb-1">✓ Sent successfully to {shareResult.sent.length} recipient{shareResult.sent.length !== 1 ? "s" : ""}</p>
                  <ul className="font-dm text-green-700 text-xs list-disc pl-4">
                    {shareResult.sent.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                </div>
                {shareResult.failed?.length > 0 && (
                  <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-4 mb-4">
                    <p className="font-dm text-amber-800 text-sm font-bold mb-1">Some emails failed:</p>
                    <ul className="font-dm text-amber-700 text-xs list-disc pl-4">
                      {shareResult.failed.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </div>
                )}
                {shareResult.share_url && (
                  <div className="bg-navy/5 rounded-lg p-3 mb-4">
                    <p className="font-dm text-navy/60 text-[11px] mb-1">Or copy the link:</p>
                    <div className="flex gap-2">
                      <input readOnly value={shareResult.share_url} className="flex-1 bg-white border border-navy/20 rounded px-2 py-1 font-mono text-[10px] text-navy/70" />
                      <button onClick={() => navigator.clipboard.writeText(shareResult.share_url)} className="bg-gold text-navy text-xs font-bold px-3 py-1 rounded">Copy</button>
                    </div>
                  </div>
                )}
                <button onClick={() => { setShareModal(false); setShareEmails([""]); setShareMessage(""); setShareResult(null); }} className="w-full bg-navy text-gold font-dm font-bold py-2.5 rounded-lg">Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
